import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Activity, ChevronRight, X } from "lucide-react";

const DISMISS_KEY = "hg_resume_dismiss_logid";

/**
 * Persistent "you have a workout in progress" banner. Hides on the Session
 * page itself (since you're already there). Tied to the in-progress
 * workout log; when the session is finished, the next fetch returns null
 * and the banner disappears on its own.
 *
 * Refetches on every route change so finishing a workout on /session/<id>
 * and navigating to /dashboard correctly clears the banner.
 */
export default function ResumeSessionBanner() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [log, setLog] = useState(null);
    const [dismissedFor, setDismissedFor] = useState(() => localStorage.getItem(DISMISS_KEY) || "");

    useEffect(() => {
        // Skip when not logged in — avoids 401 noise on /login, /register, /landing.
        if (!localStorage.getItem("hg_token")) {
            setLog(null);
            return;
        }
        let alive = true;
        api.get("/workouts/in-progress")
            .then(r => { if (alive) setLog(r.data || null); })
            .catch(() => { if (alive) setLog(null); });
        return () => { alive = false; };
    }, [pathname]);

    if (!log?.log_id) return null;
    if (pathname.startsWith("/session/")) return null;
    if (dismissedFor === log.log_id) return null;

    const exCount = Array.isArray(log.exercises) ? log.exercises.length : 0;
    const startedAt = log.started_at ? new Date(log.started_at) : null;
    const ago = startedAt ? humanize(Date.now() - startedAt.getTime()) : "";

    const resume = () => navigate(`/session/${log.log_id}`);
    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, log.log_id);
        setDismissedFor(log.log_id);
    };

    return (
        <div
            data-testid="resume-session-banner"
            className="relative w-full border-b-2 border-accent-red/70 bg-gradient-to-r from-[#0d0d0d] via-[#150505] to-[#0d0d0d] text-white"
        >
            <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
                <div className="hidden flex-none rounded-sm border border-accent-red/40 bg-accent-red/15 p-2 sm:block">
                    <Activity className="h-5 w-5 text-accent-red" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="label-eyebrow text-accent-red">Session in progress</span>
                        {ago && <span className="text-xs text-white/55" data-testid="resume-banner-ago">started {ago} ago</span>}
                    </div>
                    <p className="mt-0.5 truncate text-sm font-medium leading-snug sm:text-base">
                        <span className="font-bold text-white" data-testid="resume-banner-day">{log.day_name || "Workout"}</span>
                        <span className="text-white/65"> · {exCount} exercise{exCount === 1 ? "" : "s"} · your progress is saved.</span>
                    </p>
                </div>
                <div className="flex flex-none items-center gap-2">
                    <button
                        onClick={resume}
                        data-testid="resume-session-cta"
                        className="flex items-center gap-1.5 rounded-sm bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-[color:var(--accent-hover)] hover:shadow-[0_0_24px_-4px_rgba(239,68,68,0.7)]"
                    >
                        Resume <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={dismiss}
                        aria-label="Hide until next session"
                        data-testid="resume-session-dismiss"
                        className="rounded-sm border border-white/15 p-2 text-white/70 hover:bg-white/5 hover:text-white"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function humanize(ms) {
    const m = Math.max(0, Math.floor(ms / 60000));
    if (m < 1) return "just now";
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
}
